import Glyph from "@/components/Glyph";
import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import Constellation from "@/components/Constellation";
import { ArrowRight, Loader2, Orbit } from "lucide-react";
import { Link } from "react-router-dom";

const DETAIL = {
  sun: { label: "Sun Sign", concept: "Core Self & Ego", glyph: "☉" },
  moon: { label: "Moon Sign", concept: "Inner Emotional Waters", glyph: "☽" },
  rising: { label: "Rising", concept: "Persona & Horizon", glyph: "↑" },
};

export default function Chart() {
  const [chart, setChart] = useState(null);

  useEffect(() => {
    api.get("/chart").then(({ data }) => setChart(data)).catch(() => {});
  }, []);

  if (!chart) {
    return (
      <div className="flex justify-center py-32">
        <Loader2 className="animate-spin gold-text" size={28} />
      </div>
    );
  }

  const t = chart.trinity;

  return (
    <div className="px-5 pt-3 pb-28 fade-up">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Natal Blueprint</p>
          <h1 className="text-3xl font-light mb-1">{chart.name}'s Birth Chart</h1>
          <p className="text-sm text-[#A7B0C0]">
            {chart.birth_date}
            {chart.birth_time ? ` · ${chart.birth_time}` : ""}
            {chart.birth_place ? ` · ${chart.birth_place}` : ""}
          </p>
        </div>
        <Constellation sign={t.sun.sign} size={72} className="shrink-0" />
      </div>

      <div data-testid="sun-moon-rising-summary" className="space-y-3">
        {["sun", "moon", "rising"].map((key) => {
          const d = t[key];
          const meta = DETAIL[key];
          return (
            <div key={key} className="flex items-center gap-4 bg-[#0F1523] border border-[#D4AF37]/15 rounded-2xl px-4 py-3">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-2xl gold-glow-text shrink-0">
                <Glyph ch={meta.glyph} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] uppercase tracking-[0.2em] gold-text">{meta.label}</p>
                <p className="text-xl">
                  <Glyph ch={d.glyph} /> {d.sign} <span className="text-sm text-[#A7B0C0]">· {d.element}</span>
                </p>
                <p className="text-sm text-[#A7B0C0]">{meta.concept}</p>
                {key !== "rising" && (
                  <Link to={`/transits/${key}`} data-testid={`chart-live-${key}`} className="inline-flex items-center gap-1 text-xs gold-text mt-1 hover:underline underline-offset-4">
                    Where the {key === "sun" ? "Sun" : "Moon"} is in the sky today <ArrowRight size={12} />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Link to="/transits" data-testid="chart-live-planets" className="mt-3 flex items-center justify-between bg-[#0F1523] border border-[#D4AF37]/30 hover:border-[#D4AF37]/60 rounded-2xl p-4 transition-colors">
        <span className="flex items-center gap-2 text-base"><Orbit size={16} className="gold-text" /> All planets in today's sky</span>
        <ArrowRight size={16} className="gold-text" />
      </Link>

      <p className="text-center text-sm text-[#A7B0C0] italic mt-6">
        For a complete analysis, explore the In-Depth Readings.
      </p>
    </div>
  );
}
